import api from "./axiosInstance";

export interface TyreDto {
  id: number;
  uniqueSKU?: string;
  vendorSKU?: string;
  name?: string;
  brand?: string;
  model?: string;
  width: number;
  profile: number;
  diameter: number;
  loadIndex?: string;
  speedIndex?: string;
  spike?: string;
  strength?: string;
  season?: string;
  autoType?: string;
  countryOfOrigin?: string;
  yearOfOrigin?: string;
  provider?: string;
  providerId: number;
  providerCity?: string;
  price: number;
  residue: number;
  image?: string;
  isActive: boolean;
}

export interface TyreFilterDto {
  brand?: string;
  width?: number;
  profile?: number;
  diameter?: number;
  spike?: string;
  strength?: string;
  season?: string;
  autoType?: string;
  orderBy?: TyreOrderBy;
}

export enum TyreOrderBy {
  None = 0,
  PriceAsc = 1,
  PriceDesc = 2,
  QuantityAsc = 3,
  QuantityDesc = 4,
}

export interface PaginationRequest {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export const TyreAPI = {
  async getTyreById(id: number): Promise<TyreDto> {
    const response = await api.get(`/tyres/${id}`);
    return response.data;
  },

  async getTyres(): Promise<TyreDto[]> {
    const response = await api.get(`/tyres`);
    return response.data;
  },

  async getTyresWithFilter(
    filter: TyreFilterDto,
    pagination: PaginationRequest
  ): Promise<PaginatedResponse<TyreDto>> {
    const params = new URLSearchParams();
    
    if (filter.brand) params.append('brand', filter.brand);
    if (filter.width) params.append('width', filter.width.toString());
    if (filter.profile) params.append('profile', filter.profile.toString());
    if (filter.diameter) params.append('diameter', filter.diameter.toString());
    if (filter.spike) params.append('spike', filter.spike);
    if (filter.strength) params.append('strength', filter.strength);
    if (filter.season) params.append('season', filter.season);
    if (filter.autoType) params.append('autoType', filter.autoType);
    if (filter.orderBy !== undefined) params.append('orderBy', filter.orderBy.toString());
    
    params.append('page', pagination.page.toString());
    params.append('pageSize', pagination.pageSize.toString());
    if (pagination.sortBy) params.append('sortBy', pagination.sortBy);
    if (pagination.sortDescending !== undefined) params.append('sortDescending', pagination.sortDescending.toString());

    const response = await api.get(`/tyres/filter?${params.toString()}`);
    return response.data;
  },

  async getFeaturedTyres(count: number, filter?: TyreFilterDto): Promise<TyreDto[]> {
    const params = new URLSearchParams();
    params.append('count', count.toString());
    
    if (filter?.season) params.append('season', filter.season);

    const response = await api.get(`/tyres/featured/${count}?${params.toString()}`);
    return response.data;
  },

  async getAvailableBrands(): Promise<string[]> {
    const response = await api.get(`/tyres/brands`);
    return response.data;
  },

  async getAvailableWidths(): Promise<number[]> {
    const response = await api.get(`/tyres/widths`);
    return response.data;
  },

  async getAvailableProfiles(): Promise<number[]> {
    const response = await api.get(`/tyres/profiles`);
    return response.data;
  },

  async getAvailableDiameters(): Promise<number[]> {
    const response = await api.get(`/tyres/diameters`);
    return response.data;
  },
};

